import mergeWith from 'lodash/mergeWith';
import React, { useEffect, useReducer, useRef } from 'react';
import PropTypes from 'prop-types';
import Button from '../button/Button';
import Step1 from './Step1';
import Step2 from './Step2';
import Step3 from './Step3';
import Step4 from './Step4';
import Step5 from './Step5';
import Step6 from './Step6';
import Review from './Review';
import lendingTypes from './lendingTypes';
import * as classes from './SmeLendingForm.module.scss';
import CreditCardIntro from './CreditCardIntro';

function Steps({ state: { step, data }, dispatch, config }) {
  const onSubmit = (data, nextStep) => {
    dispatch({
      type: 'submit',
      payload: {
        step: nextStep,
        data
      }
    });
  };

  switch (step) {
    case 0:
      return (
        <div className={classes.intro}>
          {config.productName === 'credit card' ? (
            <CreditCardIntro dispatch={dispatch} />
          ) : (
            <>
              <p>Start your application for a Starling {config.productDescription}.</p>
              <p>
                We’re offering business {config.productName}s to limited companies and limited liability partnerships.
              </p>
              <p>
                The person completing this form must be a director or partner of the business applying for the facility
                and be currently authorised to act on its behalf. When we refer to “your business or your company” we
                are referring to a limited liability partnership or a private limited company as applicable.
              </p>
              <Button
                variant="secondary_solid"
                onClick={() => {
                  dispatch({
                    type: 'setStep',
                    payload: 1
                  });
                }}
              >
                Start your application
              </Button>
            </>
          )}
        </div>
      );
    case 1:
      return <Step1 onSubmit={onSubmit} />;
    case 2:
      return <Step2 config={config} onSubmit={onSubmit} />;
    case 3:
      return <Step3 config={config} onSubmit={onSubmit} />;
    case 4:
      return <Step4 isAccountHolder={data.isAccountHolder} onSubmit={onSubmit} />;
    case 5:
      return <Step5 onSubmit={onSubmit} />;
    case 6:
      return <Step6 onSubmit={onSubmit} />;
    case 7:
      return <Review config={config} data={data} />;
    default:
      return null;
  }
}

const createInitialState = (lendingType) => {
  return {
    step: 0,
    data: {
      lendingType
    }
  };
};
const reducer = (state, action) => {
  switch (action.type) {
    case 'setStep':
      return {
        ...state,
        step: action.payload
      };
    case 'submit':
      return {
        ...state,
        step: action.payload.step,
        data: {
          ...mergeWith(state.data, action.payload.data, (obj, src) =>
            Array.isArray(obj) ? obj.concat(src) : undefined
          )
        }
      };
    default:
      return state;
  }
};
function SmeLendingForm({ lendingType }) {
  const config = lendingTypes[lendingType];
  const sectionRef = useRef();
  const [state, dispatch] = useReducer(reducer, lendingType, createInitialState);
  const { step } = state;

  useEffect(() => {
    if (step > 0) {
      const headerHeight = window.innerWidth >= 1215 ? 144 : 68;
      const top = sectionRef.current.offsetTop - headerHeight;
      window.scrollTo({ top, left: 0, behavior: 'smooth' });
    }
  }, [step]);

  return (
    <section id="apply" className={classes.section} ref={sectionRef}>
      <div className={classes.content}>
        {step !== 7 && <h2>How to apply for a Starling business {config.productName}</h2>}
        <Steps state={state} dispatch={dispatch} config={config} />
      </div>
    </section>
  );
}
SmeLendingForm.propTypes = {
  lendingType: PropTypes.oneOf(['LOAN', 'OVERDRAFT', 'CREDIT_CARD']).isRequired
};

export default SmeLendingForm;
